import { toast } from "react-toastify";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputLabel,
  Paper,
  TextField
} from "@mui/material";
import { useState, useEffect, useMemo, useCallback } from "react";
import { checkThemeIdFormat, isEmpty, isUrl } from "../../../utils/Util";
import {
  GetPaymentMethods,
  GetDealerPaymentMethods,
  JWTDecode,
  TargetPayApi,
  GetIndustryCodes
} from "../../../utils/UtilTarget";
import { StyleCreator } from "../../../helpers/stylecreator";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import { useNavigate } from "react-router-dom";
import {
  DealerSurchargeProfiles,
  ICountryDetail,
  IIndustryCode,
  IPreOnBoardingRequest,
  IPricingModel
} from "../../../types/types";
import {
  COUNTRY_CODES,
  countries,
  COUNTRY_LIST
} from "../../../constants/AppConstants";
import { useGetAllDealersWithSurchargeProfileQuery } from "../../../services/API/surchargeApiSlice";
import {
  getDealerAddressInfo,
  getDealerContactInfo,
  getPricingModelDetail
} from "../../../helpers/preOnBoardingHelpers";

/* -------------------- Constants -------------------- */

const DEFAULT_LABEL = "Choose One";
const STATUS_VALID = "valid";
const PLATFORM_FEE_MIN = 3;
const PLATFORM_FEE_MAX = 3.5;
const NUMERIC_REGEX = /^[0-9]*\.?[0-9]*$/;

const defaultPricingModel: IPricingModel = {
  pricingModelId: 0,
  splitConfigurationId: "",
  description: DEFAULT_LABEL,
  markupRate: null,
  markupFee: null,
  monthlyFee: null
};

const defaultIndustry: IIndustryCode = {
  code: "",
  description: DEFAULT_LABEL
};

/* -------------------- Sub-components -------------------- */

/** Shared small-font input props to avoid repeating the inline style object. */
const smallInputProps = { style: { fontSize: "small" } } as const;

interface LabeledFieldProps {
  label: string;
  value: string | number;
  className?: string;
}

/** Read-only display field used for pricing model detail rows. */
const PricingDetailField = ({ label, value, className }: LabeledFieldProps) => (
  <Box style={formStyles.fieldSpacer}>
    <TextField
      label={label}
      className={className}
      variant="standard"
      fullWidth
      disabled
      id="outlined-required"
      value={value}
      inputProps={smallInputProps}
    />
  </Box>
);

const ValidationError = ({ message }: { message: string }) => (
  <span style={{ color: "red" }}>{message}</span>
);

/* -------------------- Module-level helpers -------------------- */

/** Resolves a canonical two-letter country code ("US" | "CA") from a raw dealer countryCode. */
function resolveCanonicalCode(rawCode: string): string {
  if (COUNTRY_CODES.US.includes(rawCode.toUpperCase())) return "US";
  if (COUNTRY_CODES.CA.includes(rawCode.toUpperCase())) return "CA";
  return "";
}

/** Fetches industry codes and payment methods for a country in parallel. */
async function fetchCountryData(
  countryCode: string
): Promise<{ codes: IIndustryCode[]; methods: any[] }> {
  const [rawCodes, rawMethods] = await Promise.all([
    GetIndustryCodes(countryCode),
    GetPaymentMethods(countryCode)
  ]);
  return {
    codes: [defaultIndustry, ...(rawCodes ?? [])],
    methods: rawMethods ?? []
  };
}

/* -------------------- Component -------------------- */

export default function PreOnBoarding() {
  const classes = StyleCreator.makeStyles(formStyles.all);
  const navigate = useNavigate();

  /* -------------------- State ---------------------- */

  const [country, setCountry] = useState<ICountryDetail>(countries[0]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [dealersList, setDealersList] = useState<any[]>([]);
  const [selectedDealer, setSelectedDealer] = useState<any | null>(null);
  const [dealerData, setDealerData] = useState<any>({});

  const [industryCodes, setIndustryCodes] = useState<IIndustryCode[]>([]);
  const [dealerIndustryCode, setDealerIndustryCode] = useState<IIndustryCode>(
    defaultIndustry
  );

  const [dealerCountry, setDealerCountry] = useState<{
    code: string;
    description: string;
  } | null>(null);
  const [isDealerCountryLocked, setIsDealerCountryLocked] = useState(false);

  const [pricingModelList, setPricingModelList] = useState<IPricingModel[]>([
    defaultPricingModel
  ]);
  const [pricingModels, setPricingModels] = useState<IPricingModel>(
    defaultPricingModel
  );

  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [dealerPaymentMethods, setDealerPaymentMethods] = useState<any[]>([]);

  const [isPlatformFeeEnabled, setIsPlatformFeeEnabled] = useState(false);
  const [platformFeePercentage, setPlatformFeePercentage] = useState<any>("");
  const [platformFeeMsg, setPlatformFeeMsg] = useState("");

  const [theme, setTheme] = useState({ themeId: "", isThemeValid: true });
  const [hostUrlState, setHostUrlState] = useState({
    hostUrl: "",
    isHostUrlValid: true
  });

  const [isDealerOnboarded, setIsDealerOnboarded] = useState(false);
  const [dealerStatus, setDealerStatus] = useState("");
  const [preOnBoardingToken, setPreOnBoardingToken] = useState("");

  const [showAchCheckbox, setShowAchCheckbox] = useState(false);
  const [isACHPaymentEnabled, setIsACHPaymentEnabled] = useState(false);

  const [locationGroupId, setLocationGroupId] = useState(0);
  const [locationGroupName, setLocationGroupName] = useState("");

  const [dealersWithProfileList, setDealersWithProfileList] = useState<
    DealerSurchargeProfiles[]
  >([]);

  // Derived — avoids repeating the condition throughout JSX
  const isUSorDefault =
    country.code === "" ||
    COUNTRY_CODES.US.includes(country.code) ||
    dealerCountry?.code === "US";

  const [
    selectedDealerWithProfile,
    setSelectedDealerWithProfile
  ] = useState<DealerSurchargeProfiles | null>(null);

  const viewPricingDetail = useCallback(() => {
    navigate(
      `../pricingmodelsetup/${pricingModels.pricingModelId}/${pricingModels.splitConfigurationId}`
    );
  }, [
    navigate,
    pricingModels.pricingModelId,
    pricingModels.splitConfigurationId
  ]);

  // RTK Query hook to get dealer list with surcharge already applied.
  const {
    data: dealersProfileList = [],
    isLoading: isDealerProfilesLoading
  } = useGetAllDealersWithSurchargeProfileQuery(null, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true
  });

  /* ------------------------------ Lifecycle -------------------------------- */

  useEffect(() => {
    document.title = "Setup a Customer";
  }, []);

  useEffect(() => {
    if (dealersProfileList.length > 0)
      setDealersWithProfileList(dealersProfileList);
  }, [dealersProfileList]);

  /* -------------------- API Helpers -------------------- */

  const getDealerForPreonboarding = useCallback(async (value: string) => {
    const response = await TargetPayApi(
      `GetDealersForPreonboarding?countryCode=${value}`,
      null,
      "get",
      "Dealer"
    );

    setDealersList(response ?? []);
  }, []);

  const getPricingModels = useCallback(async (merchantId: string) => {
    if (isEmpty(merchantId)) return;
    return TargetPayApi(
      `GetPricingModels?merchantId=${merchantId}`,
      null,
      "get"
    );
  }, []);

  const getPreOnBoardingLink = useCallback(async (dealerId: number) => {
    return TargetPayApi(
      `GetPreOnBoardingLink?dealerId=${dealerId}`,
      null,
      "get"
    );
  }, []);

  const verifyAchEnabledForMerchant = useCallback(
    async (merchantId: string) => {
      if (isEmpty(merchantId)) return;
      return TargetPayApi(`hasach/${merchantId}`, null, "get", "ACH");
    },
    []
  );

  /* -------------------- Reset Helpers -------------------- */

  const resetFormState = useCallback(() => {
    setIsDealerOnboarded(false);
    setIsPlatformFeeEnabled(false);
    setPreOnBoardingToken("");
    setPlatformFeePercentage("");
    setPlatformFeeMsg("");
    setDealerStatus("");
    setDealerIndustryCode(defaultIndustry);
    setDealerCountry(null);
    setIsDealerCountryLocked(false);
    setDealerPaymentMethods([]);
    setSelectedDealerWithProfile(null);
    setIsACHPaymentEnabled(false);
    setShowAchCheckbox(false);
    setTheme({ themeId: "", isThemeValid: true });
    setHostUrlState({ hostUrl: "", isHostUrlValid: true });
  }, []);

  const resetDealerState = useCallback(() => {
    setSelectedDealer(null);
    setDealerData({ name: "" });
    setPricingModelList([defaultPricingModel]);
    setPricingModels(defaultPricingModel);
  }, []);

  /* -------------------- Core dealer loader -------------------- */

  const getDealer = useCallback(
    async (
      dealerId: number,
      countryCodeOverride?: string,
      freshIndustryCodes?: IIndustryCode[],
      freshPaymentMethods?: any[]
    ) => {
      if (isEmpty(dealerId)) return;

      const effectiveCountryCode = countryCodeOverride ?? country.code;
      // Accept fresh data to avoid stale-closure issues
      const resolvedIndustryCodes = freshIndustryCodes ?? industryCodes;
      const resolvedPaymentMethods = freshPaymentMethods ?? paymentMethods;

      try {
        const res = await TargetPayApi(
          `GetDealerSetup?dealerId=${dealerId}&countryCode=${effectiveCountryCode}`,
          null,
          "get"
        );
        if (isEmpty(res)) return;

        const dealerResponse = {
          ...res,
          merchantId: res.merchantId ?? res.defaultMerchantId
        };

        const [rawPricingModels, methods] = await Promise.all([
          getPricingModels(dealerResponse.merchantId),
          GetDealerPaymentMethods(
            dealerId,
            dealerResponse.storeId,
            effectiveCountryCode
          )
        ]);

        // Industry code
        const industry =
          resolvedIndustryCodes.find(
            item => item.code === dealerResponse.industryCode
          ) ?? resolvedIndustryCodes[0];

        // Pre-onboarding token
        let preOnboardingToken = "";
        if (dealerResponse.verificationStatus !== STATUS_VALID) {
          const link = await getPreOnBoardingLink(dealerResponse.dealerId);
          preOnboardingToken = link
            ? `${dealerResponse.loginUrl}?onboardDealerToken=${link}`
            : "";
        }

        // Pricing models
        const splitConfigurationId =
          dealerResponse.splitConfigurationId || DEFAULT_LABEL;
        const models = [defaultPricingModel, ...(rawPricingModels ?? [])];
        const foundModel =
          models.find(
            item => item.splitConfigurationId === splitConfigurationId
          ) ?? models[0];

        // ACH — US dealers only
        if (
          dealerResponse.merchantId &&
          COUNTRY_CODES.US.includes(dealerResponse.countryCode ?? "")
        ) {
          const isAchEnabled = await verifyAchEnabledForMerchant(
            dealerResponse.merchantId
          );
          setShowAchCheckbox(isAchEnabled?.isAllowed === true);
        }

        // Batch state updates
        setDealerIndustryCode(industry);
        setDealerData(dealerResponse);
        setIsPlatformFeeEnabled(dealerResponse.isPlatformFeeEnabled);
        setDealerPaymentMethods(
          methods?.length ? methods : resolvedPaymentMethods
        );
        setPreOnBoardingToken(preOnboardingToken);
        setIsACHPaymentEnabled(dealerResponse.isACHPaymentEnabled ?? false);
        setPricingModelList(models);
        setPricingModels(foundModel);

        if (!isEmpty(dealerResponse.themeId)) {
          setTheme({ themeId: dealerResponse.themeId, isThemeValid: true });
        }
        if (!isEmpty(dealerResponse.hostUrl)) {
          setHostUrlState({
            hostUrl: dealerResponse.hostUrl,
            isHostUrlValid: true
          });
        }
        if (
          dealerResponse.platformFeePercentage != null &&
          dealerResponse.platformFeePercentage !== 0
        ) {
          setPlatformFeePercentage(dealerResponse.platformFeePercentage);
        }
        if (!isEmpty(dealerResponse.legalEntityId)) setIsDealerOnboarded(true);
        setDealerStatus(dealerResponse.verificationStatus ?? "");

        return dealerResponse;
      } catch (error) {
        console.error("Failed to fetch dealer:", error);
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      country.code,
      industryCodes,
      paymentMethods,
      getPricingModels,
      getPreOnBoardingLink,
      verifyAchEnabledForMerchant
    ]
  );

  /* -------------------- Handlers -------------------- */

  const handleCountryChange = useCallback(
    async (value: ICountryDetail) => {
      resetFormState();
      setDealersList([]);
      setIndustryCodes([]);
      setPaymentMethods([]);
      resetDealerState();

      if (value == null || isEmpty(value.code)) {
        setCountry(countries[0]);
        return;
      }

      setCountry(value);
      setIsProcessing(true);

      try {
        if (value.code === "ALL") {
          setIsDealerCountryLocked(false);
          await getDealerForPreonboarding(value.code);
        } else {
          const matched = COUNTRY_LIST.find(c => c.code === value.code) ?? null;
          setDealerCountry(matched);
          setIsDealerCountryLocked(true);

          const [, { codes, methods }] = await Promise.all([
            getDealerForPreonboarding(value.code),
            fetchCountryData(value.code)
          ]);
          setIndustryCodes(codes);
          setPaymentMethods(methods);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [resetFormState, resetDealerState, getDealerForPreonboarding]
  );

  const handleDealerChange = useCallback(
    async (dealer: any | null) => {
      resetFormState();

      setIndustryCodes([]);
      setPaymentMethods([]);
      setPricingModelList([defaultPricingModel]);
      setPricingModels(defaultPricingModel);

      if (dealer == null) {
        resetDealerState();
        return;
      }

      setIsProcessing(true);

      try {
        // check to find if the dealer has a surcharge profile configured or not to disable platform fee option fields.
        const dealerWithProfile =
          dealersWithProfileList.find(x => x.dealerId === dealer.dealerId) ??
          null;

        setSelectedDealerWithProfile(dealerWithProfile);
        setLocationGroupId(dealer.locationGroupId ?? 0);
        setLocationGroupName(dealer.groupName);

        const countryCode = country.code;

        if (!isEmpty(dealer.countryCode)) {
          const canonicalCode = resolveCanonicalCode(dealer.countryCode);

          if (!isEmpty(canonicalCode)) {
            const matched =
              COUNTRY_LIST.find(c => c.code === canonicalCode) ?? null;
            setDealerCountry(matched);
            setIsDealerCountryLocked(true);

            const { codes, methods } = await fetchCountryData(canonicalCode);
            setIndustryCodes(codes);
            setPaymentMethods(methods);
            await getDealer(dealer.dealerId, canonicalCode, codes, methods);
          } else {
            setIsDealerCountryLocked(false);
          }
        } else if (countryCode !== "ALL") {
          await getDealer(dealer.dealerId, countryCode);
        } else {
          setIsDealerCountryLocked(false);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [
      resetFormState,
      resetDealerState,
      dealersWithProfileList,
      country.code,
      getDealer
    ]
  );

  const handleDealerCountryChange = useCallback(
    async (value: { code: string; description: string } | null) => {
      setIndustryCodes([]);
      setPaymentMethods([]);
      setDealerIndustryCode(defaultIndustry);
      setPricingModelList([defaultPricingModel]);
      setPricingModels(defaultPricingModel);
      setDealerPaymentMethods([]);
      setDealerCountry(value);

      if (value == null || country.code !== "ALL") return;

      setIsProcessing(true);
      try {
        const { codes, methods } = await fetchCountryData(value.code);
        setIndustryCodes(codes);
        setPaymentMethods(methods);

        if (selectedDealer) {
          await getDealer(selectedDealer.dealerId, value.code, codes, methods);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [country.code, selectedDealer, getDealer]
  );

  const handleCheckBox = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsPlatformFeeEnabled(event.target.checked);
      if (!event.target.checked) setPlatformFeePercentage("");
    },
    []
  );

  // Prevents backspace from removing chips when the text input is empty
  const handleKeyDownEvent = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === "Backspace" &&
        (e.target as HTMLInputElement).value === "" &&
        !e.defaultPrevented
      ) {
        e.preventDefault();
      }
    },
    []
  );

  const onThemeChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trim();
      setTheme(prev => ({
        ...prev,
        themeId: value,
        isThemeValid: isEmpty(value) ? true : checkThemeIdFormat(value)
      }));
    },
    []
  );

  const onHostUrlChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trim();
      setHostUrlState(prev => ({
        ...prev,
        hostUrl: value,
        isHostUrlValid: isEmpty(value) ? true : isUrl(value)
      }));
    },
    []
  );

  const handlePlatformFeeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (NUMERIC_REGEX.test(value) || value === "") {
        setPlatformFeeMsg("");
        setPlatformFeePercentage(value);
      }
    },
    []
  );

  /* -------------------- Validation & Submit -------------------- */

  const isSaveDisabled = useMemo(() => {
    if (
      isEmpty(country.code) ||
      isEmpty(dealerData.name) ||
      isEmpty(dealerIndustryCode.code) ||
      isEmpty(pricingModels.description) ||
      pricingModels.description === DEFAULT_LABEL
    )
      return true;

    if (isPlatformFeeEnabled && isEmpty(platformFeePercentage)) return true;
    if (!dealerPaymentMethods.length) return true;
    if (!theme.isThemeValid || !hostUrlState.isHostUrlValid) return true;
    if (isProcessing) return true;
    return false;
  }, [
    country.code,
    dealerData.name,
    dealerIndustryCode.code,
    pricingModels.description,
    isPlatformFeeEnabled,
    platformFeePercentage,
    dealerPaymentMethods,
    theme.isThemeValid,
    hostUrlState.isHostUrlValid,
    isProcessing
  ]);

  function validateBeforeSubmit(): boolean {
    if (isEmpty(dealerData.name)) {
      toast.error("Missing Dealer detail");
      return false;
    }
    if (isEmpty(dealerIndustryCode.code)) {
      toast.error("Missing Industry code");
      return false;
    }
    if (
      isEmpty(pricingModels.description) ||
      pricingModels.description === DEFAULT_LABEL
    ) {
      toast.error("Missing pricing model");
      return false;
    }
    if (
      isPlatformFeeEnabled &&
      (isEmpty(platformFeePercentage) ||
        platformFeePercentage < PLATFORM_FEE_MIN ||
        platformFeePercentage > PLATFORM_FEE_MAX)
    ) {
      setPlatformFeeMsg(
        `Platform fee must be between ${PLATFORM_FEE_MIN}% - ${PLATFORM_FEE_MAX}%`
      );
      return false;
    }
    if (!dealerPaymentMethods.map(m => m.code).join(",")) {
      toast.error("Select at least one payment method");
      return false;
    }
    return true;
  }

  const createPreOnBoardingLink = useCallback(
    async (dealer: any) => {
      const payload: IPreOnBoardingRequest = {
        dealerId: dealer.dealerId,
        dmsDealerId: dealer.dmsDealerId,
        merchantId: dealer.merchantId,
        industryCode: dealerIndustryCode.code,
        splitConfigurationId: pricingModels.splitConfigurationId,
        isPlatformFeeEnabled,
        platformFeePercentage,
        paymentMethods: dealer.paymentMethods,
        createdBy: JWTDecode(localStorage.getItem("access_token"), "name"),
        themeId: theme.themeId,
        hostUrl: encodeURI(hostUrlState.hostUrl),
        loginUrl: dealer.loginUrl,
        locationGroupId,
        locationGroupName,
        dmsDealerId2: dealer.dmsDealerId2,
        isACHPaymentEnabled,
        countryCode: country.code === "ALL" ? dealerCountry.code : country.code,
        currency:
          country.code === "ALL"
            ? countries.find(c => c.code === dealerCountry.code)?.currency ?? ""
            : country.currency
      };

      const res = await TargetPayApi("CreatePreOnBoardingLink", payload);
      if (!isEmpty(res)) setPreOnBoardingToken(res);
      return res;
    },
    [
      dealerIndustryCode.code,
      pricingModels.splitConfigurationId,
      isPlatformFeeEnabled,
      platformFeePercentage,
      theme.themeId,
      hostUrlState.hostUrl,
      locationGroupId,
      locationGroupName,
      isACHPaymentEnabled,
      country.code,
      country.currency
    ]
  );

  const initiatePreBoarding = useCallback(async () => {
    if (!validateBeforeSubmit()) return;

    setIsProcessing(true);
    try {
      const payload = {
        ...dealerData,
        paymentMethods: dealerPaymentMethods.map(m => m.code).join(",")
      };
      const res = await createPreOnBoardingLink(payload);
      if (!isEmpty(res)) toast.success("Settings saved successfully");
    } finally {
      setIsProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerData, dealerPaymentMethods, createPreOnBoardingLink]);

  /* -------------------- Memoised option helpers -------------------- */

  /**
   * Set of payment method codes that are locked (already have a requestedDate).
   * Avoids running .some() once per option on every render.
   */
  const lockedPaymentCodes = useMemo(
    () =>
      new Set(
        dealerPaymentMethods
          .filter(m => !isEmpty(m.requestedDate))
          .map(m => m.code)
      ),
    [dealerPaymentMethods]
  );

  const getDealerLabel = useCallback((option: any): string => {
    const group = option.groupName?.trim();
    return `${option.name} - ${option.dmsDealerId}${
      group ? ` - ${group}` : ""
    }`;
  }, []);

  const getIndustryLabel = useCallback(
    (option: IIndustryCode): string =>
      isEmpty(option.code)
        ? option.description
        : `${option.code} - ${option.description}`,
    []
  );

  const getPricingLabel = useCallback(
    (option: IPricingModel): string =>
      !isEmpty(option.description) && option.description !== DEFAULT_LABEL
        ? getPricingModelDetail(option, "name")
        : DEFAULT_LABEL,
    []
  );

  /* -------------------- Render -------------------- */

  if (isDealerProfilesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        Please wait. Processing.....
        <CircularProgress size={25} />
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        {/* ── Main form card ── */}
        <Box display="flex" justifyContent="center" alignItems="center">
          <Paper>
            {/* Header */}
            <Box
              style={{
                padding: "5px",
                fontSize: "18px",
                color: "#3366ff",
                borderBottom: "1px solid #EAEDED"
              }}
            >
              Setup a Customer
            </Box>

            <Box style={{ margin: "20px 5px 0px 15px", fontSize: "small" }}>
              {/* Country */}
              <Box style={formStyles.fieldSpacer}>
                <Autocomplete
                  autoHighlight
                  options={countries}
                  value={country}
                  getOptionLabel={option => option?.description ?? ""}
                  style={{ width: 565, marginTop: "10px" }}
                  className={classes.autoComplete}
                  renderInput={params => (
                    <TextField
                      size="small"
                      variant="standard"
                      sx={{ fontSize: "small" }}
                      {...params}
                      label="Country *"
                      style={{ padding: "0px" }}
                      fullWidth
                    />
                  )}
                  onChange={(_, value) => handleCountryChange(value)}
                />
              </Box>

              {/* Dealer search */}
              <Box style={{ marginTop: "30px" }}>
                <Autocomplete
                  autoHighlight
                  options={dealersList}
                  value={selectedDealer}
                  getOptionLabel={getDealerLabel}
                  renderOption={(props: any, option: any) => (
                    <li
                      {...props}
                      key={option.dealerId}
                      style={{
                        borderBottom: "1px solid #ccc",
                        padding: "5px",
                        fontSize: "small"
                      }}
                    >
                      {option.name} ({option.dmsDealerId} - {option.dmsName})
                      {option.groupName.trim() !== "" && (
                        <>
                          <br />
                          {option.groupName} ({option.dealerId})
                        </>
                      )}
                      <br />
                      {getDealerAddressInfo(option)}
                      <br />
                      {getDealerContactInfo(option)}
                    </li>
                  )}
                  style={{ width: 565, marginTop: "10px" }}
                  className={classes.autoComplete}
                  fullWidth
                  onChange={(_, value) => {
                    setSelectedDealer(value);
                    handleDealerChange(value);
                  }}
                  renderInput={params => (
                    <TextField
                      size="small"
                      variant="standard"
                      sx={{ fontSize: "small" }}
                      {...params}
                      label="Search by Name or Number *"
                      style={{ padding: "0px" }}
                      fullWidth
                    />
                  )}
                />
              </Box>

              {/* Dealer's country */}
              <Box style={{ marginTop: "30px" }}>
                <Autocomplete
                  id="dealer-country-dropdown"
                  autoHighlight
                  disabled={isDealerCountryLocked}
                  options={COUNTRY_LIST}
                  value={dealerCountry}
                  getOptionLabel={option => option.description}
                  isOptionEqualToValue={(option, value) =>
                    option.code === value.code
                  }
                  style={{ width: 565, marginTop: "10px" }}
                  className={classes.autoComplete}
                  renderInput={params => (
                    <TextField
                      size="small"
                      variant="standard"
                      sx={{ fontSize: "small" }}
                      {...params}
                      label="Dealer's Country"
                      style={{ padding: "0px" }}
                      fullWidth
                    />
                  )}
                  onChange={(_, value) => handleDealerCountryChange(value)}
                />
              </Box>

              {/* Industry code */}
              <Box style={{ marginTop: "30px" }}>
                <Autocomplete
                  autoHighlight
                  disabled={isDealerOnboarded}
                  options={industryCodes}
                  value={dealerIndustryCode}
                  getOptionLabel={getIndustryLabel}
                  style={{ width: 565, marginTop: "10px" }}
                  className={classes.autoComplete}
                  renderInput={params => (
                    <TextField
                      size="small"
                      variant="standard"
                      sx={{ fontSize: "small" }}
                      {...params}
                      label="Industry Code *"
                      style={{ padding: "0px" }}
                      fullWidth
                    />
                  )}
                  onChange={(_, value) =>
                    setDealerIndustryCode(
                      isEmpty(value) ? defaultIndustry : value
                    )
                  }
                />
              </Box>

              {/* Pricing model */}
              <Box style={{ marginTop: "20px" }}>
                <InputLabel
                  size="small"
                  variant="standard"
                  sx={{ fontSize: "small" }}
                  id="pricing-model-label"
                >
                  <>
                    Pricing Model *
                    {pricingModelList.length < 1 && (
                      <CircularProgress
                        style={{ marginLeft: "10px" }}
                        size="1rem"
                      />
                    )}
                  </>
                  {!isEmpty(pricingModels.splitConfigurationId) && (
                    <button
                      style={{ marginLeft: "385px" }}
                      onClick={viewPricingDetail}
                    >
                      View Details
                    </button>
                  )}
                </InputLabel>
                <Autocomplete
                  autoHighlight
                  options={pricingModelList}
                  value={pricingModels}
                  getOptionLabel={getPricingLabel}
                  style={{ width: 565, marginTop: "15px" }}
                  className={classes.autoComplete}
                  renderInput={params => (
                    <TextField
                      size="small"
                      variant="standard"
                      sx={{ fontSize: "small" }}
                      {...params}
                      fullWidth
                    />
                  )}
                  onChange={(_, value) =>
                    setPricingModels(
                      isEmpty(value) ? pricingModelList[0] : value
                    )
                  }
                />
              </Box>

              {/* Pricing detail read-only fields */}
              <PricingDetailField
                label="Markup rate (%)"
                value={getPricingModelDetail(pricingModels, "percentage")}
                className={classes.root}
              />
              <PricingDetailField
                label="Markup fee/transaction ($)"
                value={getPricingModelDetail(pricingModels, "fee")}
                className={classes.root}
              />
              <PricingDetailField
                label="Monthly fee ($)"
                value={getPricingModelDetail(pricingModels, "monthlyFee")}
                className={classes.root}
              />

              {/* Platform fee — US only */}
              {isUSorDefault && (
                <>
                  <Box style={{ marginTop: "30px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          style={{ padding: "2px 5px", fontSize: "small" }}
                          checked={isPlatformFeeEnabled}
                          onChange={handleCheckBox}
                          disabled={!!selectedDealerWithProfile}
                        />
                      }
                      label={
                        <span style={{ fontSize: "small" }}>
                          Enable Platform fee
                        </span>
                      }
                    />
                  </Box>
                  <Box style={{ marginTop: "20px" }}>
                    <TextField
                      disabled={
                        !isPlatformFeeEnabled || !!selectedDealerWithProfile
                      }
                      label={`Platform fee %(${PLATFORM_FEE_MIN} - ${PLATFORM_FEE_MAX})`}
                      className={classes.root}
                      variant="standard"
                      fullWidth
                      required
                      id="outlined-required"
                      name="Platformfee"
                      value={platformFeePercentage}
                      inputProps={smallInputProps}
                      onChange={handlePlatformFeeChange}
                    />
                  </Box>
                  {platformFeeMsg && (
                    <Box style={{ color: "red" }}>{platformFeeMsg}</Box>
                  )}
                </>
              )}

              {/* Payment methods */}
              <Box style={formStyles.fieldSpacer}>
                <Autocomplete
                  style={{ width: "565px" }}
                  multiple
                  disableCloseOnSelect
                  options={paymentMethods}
                  value={dealerPaymentMethods || []}
                  getOptionLabel={option => option.code}
                  isOptionEqualToValue={(option, value) =>
                    option.code === value.code
                  }
                  onChange={(_, value) => setDealerPaymentMethods(value)}
                  getOptionDisabled={option =>
                    lockedPaymentCodes.has(option.code)
                  }
                  renderOption={(props, option, { selected }) => {
                    const isDisabled = lockedPaymentCodes.has(option.code);
                    return (
                      <li
                        {...props}
                        style={{ pointerEvents: isDisabled ? "none" : "auto" }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              style={{ padding: "2px 5px" }}
                              checked={selected}
                            />
                          }
                          label={option.description}
                        />
                      </li>
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      onKeyDown={handleKeyDownEvent}
                      variant="standard"
                      label="Payment Methods *"
                      InputProps={{ ...params.InputProps, endAdornment: "" }}
                      inputProps={{ ...params.inputProps, readOnly: true }}
                    />
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => {
                      const isDisabled = lockedPaymentCodes.has(option.code);
                      return (
                        <Chip
                          key={option.code}
                          label={option.code}
                          {...getTagProps({ index })}
                          disabled={isDisabled}
                          style={{
                            pointerEvents: isDisabled ? "none" : "auto",
                            color: isDisabled ? "gray" : "inherit"
                          }}
                        />
                      );
                    })
                  }
                />
              </Box>

              {/* ACH — US only */}
              {isUSorDefault && showAchCheckbox && (
                <Box style={{ marginTop: "30px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        style={{ padding: "2px 5px", fontSize: "small" }}
                        checked={isACHPaymentEnabled}
                        onChange={e => setIsACHPaymentEnabled(e.target.checked)}
                      />
                    }
                    label={
                      <span style={{ fontSize: "small" }}>
                        Enable ACH Payments
                      </span>
                    }
                  />
                </Box>
              )}

              {/* Theme ID */}
              <Box style={{ marginTop: "20px" }}>
                <TextField
                  label="Theme Id"
                  className={classes.root}
                  variant="standard"
                  fullWidth
                  name="ThemeId"
                  value={theme.themeId}
                  inputProps={smallInputProps}
                  onChange={onThemeChanged}
                />
                {!theme.isThemeValid && (
                  <ValidationError message="Expected format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX" />
                )}
              </Box>

              {/* Host URL */}
              <Box style={{ marginTop: "20px" }}>
                <TextField
                  label="Host URL"
                  className={classes.root}
                  variant="standard"
                  fullWidth
                  name="HostUrl"
                  value={hostUrlState.hostUrl}
                  inputProps={smallInputProps}
                  onChange={onHostUrlChanged}
                />
                {!hostUrlState.isHostUrlValid && (
                  <ValidationError message="Invalid URL format" />
                )}
              </Box>

              {/* Submit */}
              <Box
                style={{
                  padding: "10px 10px 10px 0px",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px"
                }}
              >
                <Button
                  disabled={isSaveDisabled}
                  style={formStyles.fieldSpacer}
                  variant="contained"
                  onClick={initiatePreBoarding}
                >
                  {isDealerOnboarded ? "Save" : "Save & Generate Token"}
                </Button>
                {isProcessing && (
                  <CircularProgress
                    size={25}
                    style={{
                      marginTop: "18px"
                    }}
                    color="info"
                  />
                )}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* ── Token display ── */}
        {(isEmpty(dealerStatus) || dealerStatus !== STATUS_VALID) &&
          !isEmpty(preOnBoardingToken) && (
            <Box display="flex" justifyContent="center">
              <Box
                style={{
                  border: "1px solid",
                  borderRadius: "5px",
                  marginTop: "20px",
                  width: "585px"
                }}
              >
                <Box
                  style={{
                    padding: "5px",
                    fontSize: "16px",
                    color: "#1976d2",
                    borderBottom: "1px solid #EAEDED"
                  }}
                >
                  Send the following link to the customer to continue onboarding
                </Box>
                <Box
                  style={{
                    margin: "5px",
                    fontSize: "small",
                    wordWrap: "break-word"
                  }}
                >
                  {preOnBoardingToken}
                </Box>
              </Box>
            </Box>
          )}
      </Box>
    </Box>
  );
}
